import axiosInstance from "../interceptor/interceptor";
import losDictionary from "../dictionary/losDictionary";
import type { EncryptedPayload } from "../../../utils/crypto";

// Body interfaces for requests
export interface LeadToApplicationBody {
  leadId?: string;
  submissionTrackingId?: string;
  formData?: any;
  // Add more properties as needed based on your API requirements
}

export interface UpdateLeadBody {
  trackingId: string;
}

export interface CreateUpdateApplicantBasicDetailsBody {
  applicantId?: any;
  submissionTrackingId?: string;
  basicDetails?: any;
  formData?: any;
  // Add more properties as needed based on your API requirements
}

export interface CreateUpdateApplicantDemographicDetailsBody {
  applicantId?: any;
  isPermanentNotSameAsCurrentFlag?: boolean;
  currentAddress?: any;
  permanentAddress?: any;
  // Add more properties as needed based on your API requirements
}

export interface EmailAndMobileVerificationBody {
  applicantId?: number;
  submissionTrackingId?: string;
  email?: string;
  mobileNumber?: string;
  emailVerified?: boolean;
  mobileVerified?: boolean;
  // Add more properties as needed based on your API requirements
}

/** Plain body or hybrid-encrypted payload from `encryptData`. */
export type PanVerificationBody =
  | EncryptedPayload
  | {
      applicantId?: number;
      applicantHasPanCardFlag?: boolean;
      applicantPanNo?: string;
      applicationId?: number;
      trackingId?: string;
      eventName?: string;
      userApplicantType?: string;
    };

export interface SaveEmploymentDetailsBody {
  applicantId?: any;
  submissionTrackingId?: string;
  employmentDetails?: any;
  formData?: any;
  // Add more properties as needed based on your API requirements
}

export interface SaveAcademicDetailsBody {
  applicantId?: any;
  submissionTrackingId?: string;
  academicDetails?: any;
  formData?: any;
  // Add more properties as needed based on your API requirements
}

export interface SaveExpenseBody {
  trackingId: string;
  eventName: string;
  applicantType: string;
  applicationId: string | number;
  queryId?: string;
  livingExpenses?: number;
  livingCardExpenses?: number;
  travelExpenses?: number;
  otherExpenses?: number;
  tuitionFees?: number;
  totalExpenses?: number;
  loanRequestedAmt?: number;
  ownSourceAmt?: number;
  scholarshipAmt?: number;
  otherIncomeAmt?: number;
  totalFundsRequiredAmt?: number;
}

export interface SaveReferenceBody {
  trackingId: string;
  eventName: string;
  applicantType: string;
  applicationId: number | string;
  emailAddress?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  active?: boolean;
  mobileNo?: number | string;
  queryId?: string;
  referenceAddress?: {
    addressTypeDescription?: string;
    addressTypeId?: string;
    landmark?: string;
    addressLine1?: string;
    cityId?: string;
    cityName?: string;
    countryId?: string;
    countryName?: string;
    pincodeId?: number | string;
    pincode?: string;
  };
}
export interface UpdateReferenceBody {
  trackingId: string;
  eventName: string;
  applicantType: string;
  applicationId: number | string;
  emailAddress?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  referenceId?: number | string;
  active?: boolean;
  mobileNo?: number | string;
  queryId?: string;
  referenceAddress?: {
    addressTypeDescription?: string;
    addressTypeId?: string;
    landmark?: string;
    addressLine1?: string;
    cityId?: string;
    cityName?: string;
    countryId?: string;
    countryName?: string;
    pincodeId?: number | string;
    pincode?: string;
  };
}
export interface SaveCollateralBody {
  trackingId?: string;
  eventName?: string;
  applicantType?: string;
  applicationId: number | string;
  collateralAddress?: {
    addressId?: number | string | null;
    addressLine1?: string;
    addressLine2?: string;
    area?: string;
    landmark?: string;
    cityId?: string;
    cityName?: string;
    countryId?: string;
    countryName?: string;
    pincode?: string;
    pincodeId?: number | string;
    stateId?: string;
    stateName?: string;
    addressTypeDescription?: string;
    addressTypeId?: string;
  };
  collateralAddressId?: number | string | null;
  collateralId?: number | string | null;
  collateralType?: string;
  collateralTypeId?: number | string | null;
  collateralValue?: number | null;
  fdrNumber?: number | string | null;
  nameFdHolder?: string;
  accountNumber?: number | string | null;
  holderName?: string;
  noOfUnits?: number | string | null;
  unitPrice?: number | null;
  propertyTypeId?: number | string | null;
  propertySubTypeId?: number | string | null;
  propertyValue?: number | null;
  collateralOwnershipDetailsDTOList?: Array<{
    collateralOwnerDetailsId?: number | string | null;
    collateralId?: number | string | null;
    applicationId?: number | string;
    applicantId?: number | string | null;
    applicationDisplayCode?: string | null;
    bankCustomer?: string | null;
    ownershipPercentage?: number | null;
    phoneNumber?: string;
    ownerAddressId?: number | string | null;
    ownerAddress?: string | null;
    coOwnerApplicantCIF?: string | null;
    coOwnerName?: string;
    active?: boolean;
    createdBy?: number | string | null;
    createdOn?: string | null;
    modifiedBy?: number | string | null;
    modifiedOn?: string | null;
  }>;
  propertySequence?: number | null;
  fdSequence?: number | null;
  savingSequence?: number | null;
}
export interface CreateCollateralBody {
  formData: any[];
  trackingId: string;
  collateralSequenceType: string;
}
export interface SaveLoanRequirementBody {
  applicationId: number | string;
  trackingId: string;
  eventName: string;
  applicantType: string;
  amountRequested?: number | string;
  psiAmount?: number | string;
  loantenure?: number | string;
  requiredRoi?: number | string;
  queryId?: string;
  caseType?: string;
  expectedDateOfDisbursement?: string;
  promoCode?: string;
  isSiblingTag?: string;
}

export interface SaveUpdateEntranceExamsDetailsBody {
  applicantId?: any;
  submissionTrackingId?: string;
  entranceExamsDetails?: any;
  formData?: any;
  // Add more properties as needed based on your API requirements
}

export interface SaveCoApplicantBody {
  applicantId?: string;
  submissionTrackingId?: string;
  applicationId?: any;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobileNumber?: string;
  dob?: string;
  gender?: string;
  relationshipWithStudent?: string;
  incomeConsidered?: boolean;
  coApplicantDetails?: any;
  formData?: any;
  // Add more properties as needed based on your API requirements
}

export interface CreateApplicationBody {
  // Add your interface properties here
  // Example:
  // applicationData: any;
  // submissionTrackingId: string;
}

export interface UpdateApplicationBody {
  // Add your interface properties here
  // Example:
  // id: string;
  // updateData: any;
}

/** Response body for GET `/api/v1/processio/pii/getKey` (PEM public key; shape may vary by env). */
export type PiiGetKeyResponseBody = {
  data?: string | { publicKey?: string };
  message?: string;
  success?: boolean;
};

/** Response body for GET `/api/v1/processio/get-reference-ids/{trackingId}`. */
export type GetReferenceIdsResponseBody = {
  status?: string;
  data?: {
    referenceOneId?: number | string | null;
    referenceTwoId?: number | string | null;
  };
};

const losServices = {
  // Lead to Application conversion API
  leadToApplication: (payload: LeadToApplicationBody) =>
    axiosInstance.post(losDictionary.leadToApplication(), payload),

  // Update Lead API
  updateLead: (payload: UpdateLeadBody) =>
    axiosInstance.post(losDictionary.updateLead(), payload),

  // Create/Update Applicant Basic Details API
  createUpdateApplicantBasicDetails: (
    payload: CreateUpdateApplicantBasicDetailsBody
  ) =>
    axiosInstance.post(
      losDictionary.createUpdateApplicantBasicDetails(),
      payload
    ),

  // Create/Update Applicant Demographic Details API
  createUpdateApplicantDemographicDetails: (
    payload: CreateUpdateApplicantDemographicDetailsBody
  ) =>
    axiosInstance.post(
      losDictionary.createUpdateApplicantDemographicDetails(),
      payload
    ),

  // Email and Mobile Verification API
  emailAndMobileVerification: (payload: EmailAndMobileVerificationBody) =>
    axiosInstance.post(losDictionary.emailAndMobileVerification(), payload),

  // PAN Verification API
  panVerification: (payload: PanVerificationBody) =>
    axiosInstance.post(losDictionary.panVerification(), payload),

  // Save Employment Details API
  saveEmploymentDetails: (payload: SaveEmploymentDetailsBody) =>
    axiosInstance.post(losDictionary.saveEmploymentDetails(), payload),

  // Save Expense and Funds API
  saveExpense: (payload: SaveExpenseBody) =>
    axiosInstance.post(losDictionary.saveExpense(), payload),

  // Save Reference details API
  saveReference: (payload: SaveReferenceBody) =>
    axiosInstance.post(losDictionary.saveReference(), payload),
  // Update Reference details API
  updateReference: (payload:UpdateReferenceBody) =>
    axiosInstance.post(losDictionary.updateReference(), payload), 
  // Save Collateral details API
  saveCollateral: (payload: SaveCollateralBody) =>
    axiosInstance.post(losDictionary.saveCollateral(), payload),
  createCollateral: (payload: CreateCollateralBody) =>
    axiosInstance.post(losDictionary.createCollateral(), payload),

  // Save Loan Requirement API
  saveLoanRequirement: (payload: SaveLoanRequirementBody) =>
    axiosInstance.post(losDictionary.saveLoanRequirement(), payload),

  // Save Academic Details API
  saveAcademicDetails: (payload: SaveAcademicDetailsBody) =>
    axiosInstance.post(losDictionary.saveAcademicDetails(), payload),

  // Save/Update Entrance Exams Details API
  saveUpdateEntranceExamsDetails: (
    payload: SaveUpdateEntranceExamsDetailsBody
  ) =>
    axiosInstance.post(losDictionary.saveUpdateEntranceExamsDetails(), payload),

  // Save Co-Applicant API
  // saveCoApplicant: (payload: SaveCoApplicantBody) =>
  //   axiosInstance.post(losDictionary.saveCoApplicant(), payload),

  getCoApplicantFields: () =>
    axiosInstance.get(losDictionary.getCoApplicantFields()),

  storeUserFields: (payload: any) =>
    axiosInstance.post(losDictionary.storeUserFields(), payload),

  deleteUserFields: (payload: { trackingId: string; form_field_ids: number[] }) =>
    axiosInstance.post(losDictionary.deleteUserFields(), payload),

  saveCoApplicantFormData: (payload: any) =>
    axiosInstance.post(losDictionary.saveCoApplicantFormData(), payload),

  relationMaster: () => axiosInstance.get(losDictionary.relationMaster()),

  /** GET ProcessIO PII public key (PEM). Uses `REACT_APP_API_URL` + `/api/v1/processio/pii/getKey`. */
  getPiiKey: () =>
    axiosInstance.get<PiiGetKeyResponseBody>(losDictionary.piiGetKey()),

  getCoApplicantDetails: (payload: any) =>
    axiosInstance.post(losDictionary.getCoApplicantDetails(), payload),

  // Get existing Reference One / Reference Two IDs for a tracking ID
  getReferenceIds: (trackingId: string) =>
    axiosInstance.get<GetReferenceIdsResponseBody>(
      losDictionary.getReferenceIds(trackingId),
    ),

  // Fetch latest ProcessIO lead data by tracking ID — called before
  // updateLead so the update is applied on top of the current lead state.
  getDataByLeadId: (trackingId: string) =>
    axiosInstance.post(losDictionary.getDataByLeadId(), { trackingId }),

  // Add more LOS service methods here
  // Example methods:
  // createApplication: (payload: CreateApplicationBody) =>
  //   axiosInstance.post(losDictionary.createApplication(), payload),
  //
  // updateApplication: (id: string, payload: UpdateApplicationBody) =>
  //   axiosInstance.put(losDictionary.updateApplication(id), payload),
  //
  // getApplication: (id: string) =>
  //   axiosInstance.get(losDictionary.getApplication(id)),
  //
  // submitApplication: (payload: any) =>
  //   axiosInstance.post(losDictionary.submitApplication(), payload),
  //
  // getApplicationStatus: (id: string) =>
  //   axiosInstance.get(losDictionary.getApplicationStatus(id)),
};

export default losServices;
